import './Login.scss'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { emailLoginPattern, onBackKeyPress } from '@utils/helpers'
import { setProfileButtonFocus } from '@actions/profileActions'
import { useDispatch } from 'react-redux'
import { loginRoutes, routes } from '@constants/routes.const'
import { LoginInputField } from '@components/Login/LoginInputField/LoginInputField'
import { SecondaryButton } from '@components/Buttons/SecondaryButton/SecondaryButton'
import { Header } from '@components/Header/Header'
import DotSpinner from '@components/DotSpinner/DotSpinner'
import { ErrorActions, ErrorCodes, KeyName } from '@constants/index'
import { useTypedSelector } from '../../store'
import { ShowPassword } from '@components/ShowPassword/ShowPassword'
import Text from '@components/Text/Text'
import { TextTag } from '@enums/textTag'
import { setPageNotFound } from '@actions/pageNotFoundActions'
import { setError } from '@actions/errorCodeActions'
import { getLanguage } from '@utils/vocab'
import { fetchMenu } from '@actions/menuActions'
import { VelvetSDK } from 'sdk'

interface LoginProps {
  showMenu: (status: boolean) => void
}

export const Login = ({ showMenu }: LoginProps) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const inputRef = useRef<HTMLInputElement>(null)
  const showPasswordRef = useRef<HTMLFormElement>(null)
  const [emailErrorMessage, setEmailErrorMessage] = useState<string | null>(null)
  const [passwordErrorMessage, setPasswordErrorMessage] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [userEmail, setUserEmail] = useState('')
  const [userPassword, setUserPassword] = useState('')
  const [shownField, setShownField] = useState('email')
  const [loading, setLoading] = useState(false)
  const V = VelvetSDK.Vocabulary.vocabulary
  const [isMenuFetching, setIsMenuFetching] = useState(true)
  const [isVocabFetching, setIsVocabFetching] = useState(true)
  const config = VelvetSDK.Configuration.configuration
  const apiEndpointBaseUrl = config.apiEndpointBaseUrl
  const menuData = useTypedSelector((state) => state.menu.data.mainMenu.content)

  const closeLoginScreen = useCallback(
    (e: KeyboardEvent) => {
      onBackKeyPress(e, () => {
        shownField === 'email' && navigate(routes.INITIAL_PAGE)
      })
    },
    [navigate, shownField],
  )

  const getUsersLang = useCallback(async () => {
    return await getLanguage()
  }, [])

  const login = useCallback(
    async (mail: string, pass: string) => {
      try {
        if (apiEndpointBaseUrl) {
          const response = await VelvetSDK.Authentication.service.login(mail, pass)
          return response
        }
      } catch (error) {
        console.error('Error with login process', error)
      }
    },
    [apiEndpointBaseUrl],
  )

  const fetchVocabulary = useCallback(
    async (language?: string | null) => {
      await VelvetSDK.Vocabulary.service.fetchVocabulary(language)

      if (VelvetSDK.Vocabulary.vocabulary.error) {
        const error = VelvetSDK.Vocabulary.vocabulary.error
        const errorUpdate = {
          showErrorModal: true,
          errorCode: ErrorCodes.FETCH_VOCABULARY,
          HTTPcode: error.status,
          fetchAction: ErrorActions.VOCABULARY,
        }

        dispatch(setError(errorUpdate))
      }
    },
    [dispatch],
  )

  const handleLogin = useCallback(async () => {
    dispatch(setPageNotFound(false))
    setLoading(true)

    const user = await login(userEmail, userPassword)
    const language = user && (await getUsersLang())
    if (user && user.userID) {
      if (language) {
        await dispatch(fetchMenu(language))
        menuData.length > 1 && setIsMenuFetching(false)
        await fetchVocabulary(language).then(() => setIsVocabFetching(VelvetSDK.Vocabulary.isVocabFetching))
      }

      !isVocabFetching && !isMenuFetching && setLoading(false)
      !loading && navigate(routes.MANAGE_PROFILES)
    } else {
      setLoading(false)
      navigate(`${routes.LOGIN}/${loginRoutes.ACCOUNT_ERROR}`)
    }
  }, [
    dispatch,
    fetchVocabulary,
    getUsersLang,
    isMenuFetching,
    isVocabFetching,
    loading,
    login,
    menuData,
    navigate,
    userEmail,
    userPassword,
  ])

  const checkEmail = useCallback(
    async (e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e?.preventDefault()

      try {
        const signInMethods = await VelvetSDK.Authentication.service.isEmailValid(userEmail)

        if (signInMethods?.length === 0 && emailLoginPattern.test(userEmail)) {
          setEmailErrorMessage(V?.loginErrorMessageOne)
        } else if (emailLoginPattern.test(userEmail)) {
          setShownField('password')
          setEmailErrorMessage(null)
        }
      } catch (error) {
        console.error('Error fetching sign-in methods:', error)
      }

      if (emailLoginPattern.test(userEmail) === false) {
        setEmailErrorMessage(V?.loginErrorMessageTwo)
      }
    },
    [V?.loginErrorMessageOne, V?.loginErrorMessageTwo, userEmail],
  )

  const checkPassword = useCallback(
    (e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e?.preventDefault()

      if (userPassword.length > 0) {
        handleLogin()
        setUserEmail('')
        setUserPassword('')
        setPasswordErrorMessage(null)
      }
    },
    [handleLogin, userPassword.length],
  )

  const handleUserEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserEmail(e.target.value)
  }

  const handleUserPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserPassword(e.target.value)
  }

  const navigateToForgotPassword = useCallback(() => {
    navigate(`${routes.LOGIN}/${loginRoutes.FORGOT_PASSWORD}`)
  }, [navigate])

  const navigateToPinAndPair = useCallback(() => {
    navigate(`${routes.LOGIN}/${loginRoutes.PIN_AND_PAIR}`)
  }, [navigate])

  const navigateToShowPassword = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (shownField === 'password' && e.key === KeyName.ArrowRight) {
        if (inputRef.current?.value.length === inputRef.current?.selectionEnd) {
          inputRef.current?.blur()
          showPasswordRef.current?.focus()
        }
      }
    },
    [shownField],
  )

  useEffect(() => {
    const currentInputRef = inputRef?.current
    currentInputRef?.focus()
  }, [])

  useEffect(() => {
    showMenu(false)
    dispatch(setProfileButtonFocus(V?.infoProfileTitle))

    return () => {
      showMenu(true)
    }
  }, [showMenu, dispatch, V?.infoProfileTitle])

  useEffect(() => {
    document.addEventListener('keydown', closeLoginScreen, false)
    return () => document.removeEventListener('keydown', closeLoginScreen, false)
  })

  const renderShownField = useMemo(() => {
    switch (shownField) {
      case 'email':
        return (
          <div className="login__email-input">
            <LoginInputField
              type="text"
              label={V?.loginEnterYourEmail}
              inputValue={userEmail}
              errorMessage={emailErrorMessage}
              onChange={handleUserEmail}
              onSubmit={checkEmail}
              inputRef={inputRef}
              placeholder={V?.createAccountEmailPlaceholder}
            />
            <div className="login__button-container">
              <SecondaryButton onClick={checkEmail}>{V?.continueButtonLabel}</SecondaryButton>
              <Text tag={TextTag.P}>{V?.loginWithBrowserLabel}</Text>
              <SecondaryButton onClick={navigateToPinAndPair}>{V?.loginWithBrowserButtonLabel}</SecondaryButton>
            </div>
          </div>
        )
      case 'password':
        return (
          <div className="login__password-input">
            <div className="login__wrapper">
              <LoginInputField
                type={showPassword ? 'text' : 'password'}
                label={V?.loginEnterPasswordLabel}
                inputValue={userPassword}
                errorMessage={passwordErrorMessage}
                onChange={handleUserPassword}
                onSubmit={checkPassword}
                placeholder={V?.createAccountPasswordPlaceholder}
                inputRef={inputRef}
                navigateToShowPassword={navigateToShowPassword}
              />
              <ShowPassword setShowPassword={setShowPassword} showPassword={showPassword} ref={showPasswordRef} />
            </div>
            <div className="login__button-container">
              <SecondaryButton onClick={checkPassword}>{V?.continueButtonLabel}</SecondaryButton>
              <SecondaryButton onClick={navigateToForgotPassword}>{V?.loginForgotPassword}</SecondaryButton>
            </div>
          </div>
        )
    }
  }, [
    V?.continueButtonLabel,
    V?.createAccountEmailPlaceholder,
    V?.createAccountPasswordPlaceholder,
    V?.loginEnterPasswordLabel,
    V?.loginEnterYourEmail,
    V?.loginForgotPassword,
    V?.loginWithBrowserButtonLabel,
    V?.loginWithBrowserLabel,
    checkEmail,
    checkPassword,
    emailErrorMessage,
    navigateToForgotPassword,
    navigateToPinAndPair,
    navigateToShowPassword,
    passwordErrorMessage,
    showPassword,
    shownField,
    userEmail,
    userPassword,
  ])

  const navigateBackToEmailSection = (e: KeyboardEvent) =>
    onBackKeyPress(e, () => {
      setShownField('email')
    })

  useEffect(() => {
    document.addEventListener('keydown', navigateBackToEmailSection, false)

    return () => document.removeEventListener('keydown', navigateBackToEmailSection, false)
  })

  return (
    <div className="login">
      <Header label="LogIn" />
      {loading ? <DotSpinner /> : renderShownField}
    </div>
  )
}
